<template>
  <div id="gjs"></div>
</template>

<script>
import 'grapesjs/dist/css/grapes.min.css';
import grapesBasic from 'grapesjs-blocks-basic';
import grapesCustomCode from 'grapesjs-custom-code';
import grapesExport from 'grapesjs-plugin-export';
import grapesjs from 'grapesjs';

import { store } from '@/utils/common';
import { bizmatesApi } from '@/utils/bizmatesApi';
import qs from 'qs';

export default {
  data() {
    return {
      editor: null,
      rankId: null,
      lessonId: null,
      isCreate: false,
      isAdmin: false,
      disableSaving: true
    }
  },
  beforeMount() {
    this.$i18n.locale = 'en';
  },
  mounted() {
    this.editor = grapesjs.init({
      container: '#gjs',
      plugins: [grapesBasic, grapesExport, grapesCustomCode],
      allowScripts: 1,
      fromElement: true,
      height: '100vh',
    });

    // add devices
    const deviceManager = this.editor.Devices;
    deviceManager.remove('tablet');
    deviceManager.remove('mobileLandscape');
    deviceManager.remove('mobilePortrait');

    deviceManager.add({
      id: 'mobilePortrait',
      name: 'Mobile Portrait',
      width: '375px',
      height: '667px',
      widthMedia: '375px',
      priority: 375,
    });

    deviceManager.add({
      id: 'mobileLandscape',
      name: 'Mobile Landscape',
      width: '460px',
      height: '90vh',
      widthMedia: '460px',
      priority: 460,
    });

    deviceManager.add({
      id: 'tablet',
      name: 'Tablet',
      width: '768px',
      height: '90vh',
      widthMedia: '768px',
      priority: 768,
    });

    // add custom buttons
    const editor = this.editor;
    const vm = this;
    vm.customEditCode(editor);

    editor.Panels.addButton('options', [
      {
        id: 'edit',
        className: 'fa fa-edit',
        command: 'edit-code',
        attributes: { title: 'Edit Code' },
      },
      {
        id: 'save',
        className: 'fa fa-floppy-o icon-blank',
        attributes: { title: 'Save Template' },
        command(editor) {
          vm.save(editor);
        },
      },
      {
        id: 'approve',
        className: 'fa fa-check icon-blank',
        attributes: { title: 'Approve Changes' },
        command() {
          vm.approve();
        }
      }
    ]);

    this.lessonId = this.$route.params.lessonId;
    this.rankId = this.$route.params.rankId;
    this.isCreate = this.$route.params.isCreate;
    if (this.isCreate && !store.getHtmlBuilderInsertStatus(this.lessonId)) {
      store.setHtmlBuilderInsertStatus(this.lessonId, false);
      this.editor.setComponents('');
      this.editor.setStyle('');
    } else {
      this.fetchMaterial(this.lessonId, this.rankId);
    }
  },
  methods: {
    async approve() {
      if (this.disableSaving) {
        this.showMessage('warning', this.$t('htmlBuilder.disabled'));
        return;
      }

      if (!this.isAdmin) {
        this.showMessage('warning', this.$t('htmlBuilder.notAllowed'));
        return false;
      }
      let data = {
        l: this.lessonId,
        r: this.rankId,
      };
      await bizmatesApi.post(
        'lessonmaterials/approve',
        qs.stringify(data),
        {},
        (response) => {
          if (response.data.status) {
            this.showMessage('success', this.$t('htmlBuilder.approved'));
          }
        },
        () => {
          this.showMessage('error', this.$t('htmlBuilder.unauthorized'));
        }
      );
    },
    async save(editor) {
      if (this.disableSaving) {
        this.showMessage('warning', this.$t('htmlBuilder.disabled'));
        return;
      }

      let message = '';
      let url = '';
      let data = {
        css: editor.getCss(),
        html: editor.getHtml(),
        l: this.lessonId,
        r: this.rankId,
      };

      if (this.isCreate && !store.getHtmlBuilderInsertStatus(this.lessonId)) {
        url = 'lessonmaterials/insert_html';
        store.setHtmlBuilderInsertStatus(this.lessonId, true);
        message = this.$t('htmlBuilder.create');
      } else {
        url = 'lessonmaterials/update_html';
        message = this.$t('htmlBuilder.update');
      }

      if (!data.html || !data.css) {
        store.setHtmlBuilderInsertStatus(this.lessonId, false);
        this.showMessage('warning', this.$t('htmlBuilder.error'));
        return;
      }

      await bizmatesApi.post(
        url,
        qs.stringify(data),
        {},
        (response) => {
          if (response.data.status) {
            this.showMessage('success', message);
          }
        },
        () => {
          this.showMessage('error', this.$t('htmlBuilder.unauthorized'));
        }
      );
    },

    async fetchMaterial(lessonId, rankId) {
      const params = { l: lessonId, r: rankId, cms: 1 };
      await bizmatesApi.get(
        'lessonmaterials/html',
        { params },
        (response) => {
          this.editor.setComponents(response.data.content);
          this.editor.setStyle(response.data.css);
          this.isAdmin = response.data.admin || false;
        },
        () => {
          this.$router.push('/error');
        }
      );
    },

    showMessage(type, message) {
      this.$notify({
        title: 'Notice',
        message: message,
        type: type,
        position: 'top-left',
        duration: 5000
      });
    },

    // Edit code for html and css
    customEditCode(editor) {
      const pfx = editor.getConfig().stylePrefix;
      const modal = editor.Modal;
      const htmlCodeViewer = editor.CodeManager.getViewer('CodeMirror').clone();
      const cssCodeViewer = editor.CodeManager.getViewer('CodeMirror').clone();
      const container = document.createElement('div');
      const btnEdit = document.createElement('button');

      htmlCodeViewer.set({
        codeName: 'htmlmixed',
        readOnly: 0,
        theme: 'hopscotch',
        autoBeautify: true,
        autoCloseTags: true,
        autoCloseBrackets: true,
        lineWrapping: true,
        styleActiveLine: true,
        smartIndent: true,
        indentWithTabs: true
      });

      cssCodeViewer.set({
        codeName: 'css',
        readOnly: 0,
        theme: 'hopscotch',
        autoBeautify: true,
        autoCloseTags: true,
        autoCloseBrackets: true,
        lineWrapping: true,
        styleActiveLine: true,
        smartIndent: true,
        indentWithTabs: true
      });

      btnEdit.innerHTML = 'Save';
      btnEdit.className = pfx + 'btn-prim ' + pfx + 'btn-import';
      btnEdit.onclick = () => {
        const html = htmlCodeViewer.editor.getValue();
        const css = cssCodeViewer.editor.getValue();
        editor.DomComponents.getWrapper().set('content', '');
        editor.setComponents(html.trim());
        editor.setStyle(css);
        modal.close();
      };

      editor.Commands.add('edit-code', {
        run: function (editor, sender) {
          sender && sender.set('active', 0);
          let htmlViewer = htmlCodeViewer.editor;
          let cssViewer = cssCodeViewer.editor;
          modal.setTitle('Edit code');

          if (!htmlViewer && !cssViewer) {
            const txtarea = document.createElement('textarea');
            const cssarea = document.createElement('textarea');
            const htmlLabel = document.createElement('div');
            const cssLabel = document.createElement('div');
            htmlLabel.innerHTML = 'HTML';
            cssLabel.innerHTML = 'CSS';

            container.appendChild(htmlLabel);
            container.appendChild(txtarea);
            container.appendChild(cssLabel);
            container.appendChild(cssarea);
            container.appendChild(btnEdit);
            htmlCodeViewer.init(txtarea);
            cssCodeViewer.init(cssarea);
            htmlViewer = htmlCodeViewer.editor;
            cssViewer = cssCodeViewer.editor;
          }

          const htmlContent = editor.getHtml();
          const cssContent = editor.getCss();
          modal.setContent('');
          modal.setContent(container);
          htmlCodeViewer.setContent(htmlContent);
          cssCodeViewer.setContent(cssContent);
          modal.open();
          htmlViewer.refresh();
          cssViewer.refresh();
        }
      });
    }
  }
};
</script>
